import React from 'react';
import {
    priceSale,
    toGel,
    type Discount,
    type DiscountProblemKind,
} from './discountMath';
import type { PosProduct, PosVariant } from './posProduct';
import { variantLabel } from './sellerMeta';

/**
 * One cart line: one variant of a product plus the quantity being sold. Lines
 * are keyed by variant, so two variants of one product are two lines.
 */
export type CartLine = {
    product: PosProduct;
    variant: PosVariant;
    qnt: number;
    /** The line's own discount as entered; it leaves with the line. */
    discount: Discount | null;
};

/**
 * A discount that no longer fits after a cart change (see `discountMath`):
 * the discount stays, and the seller edits or clears it before paying.
 * `variantId` names the line for a line discount's problem; it is absent
 * when the problem is the sale's.
 */
export type DiscountProblem = {
    kind: DiscountProblemKind;
    variantId?: string;
};

/** One row of the variant picker. */
export type VariantOption = {
    variantId: string;
    /** What tells this variant apart (`Black / L`): see `variantLabel`. */
    label: string;
    price: number;
    sellableQuantity: number;
    /** Units of this variant the cart already holds. */
    inCartQty: number;
    /**
     * Whether choosing it would add anything: `outOfStock` has nothing
     * sellable in this branch, `allInCart` has all its sellable stock in the
     * cart already. Either way it cannot be chosen.
     */
    availability: VariantAvailability;
};

export type VariantAvailability = 'available' | 'outOfStock' | 'allInCart';

/** The open variant picker: the tapped product and one row per variant. */
export type VariantPicker = {
    product: PosProduct;
    options: VariantOption[];
    /**
     * The variant it opens on: the first one that can be chosen among those
     * the scan named (every variant, for a tile tap or a search), else the
     * first one that can be chosen at all; `null` when none can.
     */
    initialVariantId: string | null;
};

/**
 * What adding a product the seller named did. `outOfStock`: none of what was
 * named has anything sellable. `atLimit`: the cart already holds all the
 * sellable stock of what was named, so nothing changed.
 */
export type AddProductOutcome =
    | { type: 'added' }
    | { type: 'variantPicker' }
    | { type: 'outOfStock' }
    | { type: 'atLimit' };

/**
 * What `addByBarcode` did: an add outcome, or `notFound` when no variant's
 * barcode is the code. Barcodes only: whatever the search box shows is search
 * `Enter`'s business (`useSearchEnterToAdd`), not the scan's.
 */
export type AddByBarcodeOutcome = AddProductOutcome | { type: 'notFound' };

/** The product catalog (from the API) the state operates over. */
export type SellerCatalog = {
    products: PosProduct[];
    isLoading: boolean;
    isError: boolean;
    /** The server hit its row cap, so this catalog is not the whole one. */
    truncated: boolean;
};

export type SellerContextValue = {
    /* Browse */
    search: string;
    setSearch: (value: string) => void;
    /** Selected category ids (API category ids); empty means "all categories". */
    selectedCategoryIds: string[];
    setSelectedCategoryIds: (ids: string[]) => void;
    filteredProducts: PosProduct[];
    productsLoading: boolean;
    productsError: boolean;
    /** The catalog was cut short by the server's row cap. */
    productsTruncated: boolean;

    /* Cart */
    cart: CartLine[];
    cartCount: number;
    /** Units of a product in the cart, across all of its variants. */
    inCartQty: (productId: string) => number;
    /**
     * A tile tap: adds a single-variant product, and opens the variant picker
     * for a several-variant one. Returns which of the two it did, so the grid
     * sends focus home only after an add by pointer, never when the picker
     * opens.
     */
    selectProduct: (product: PosProduct) => 'added' | 'variantPicker';
    setLineQnt: (variantId: string, qnt: number) => void;
    removeLine: (variantId: string) => void;
    clearCart: () => void;
    /**
     * A scanned or typed code, matched against variant barcodes only
     * (trimmed, case-insensitive). A code naming one variant adds that
     * variant; one that several variants of a product share opens the
     * picker. When several products share it, one with something left to add
     * wins over one at its limit, and that over one out of stock; catalog
     * order breaks a tie. The single operation a hardware scanner (future
     * native shell) will call too, so it never reads the search box.
     */
    addByBarcode: (code: string) => AddByBarcodeOutcome;
    /**
     * Adds a product the seller named by search (search `Enter` with one
     * result left): the same rules and outcomes as a scan naming every
     * variant of it, so a single-variant product adds its variant and a
     * several-variant one opens the picker.
     */
    addProduct: (product: PosProduct) => AddProductOutcome;

    /* Variant picker */
    /** The open picker, or `null` when none is open. */
    variantPicker: VariantPicker | null;
    /** Adds the chosen variant and closes the picker; one that can't be chosen (see `VariantOption.availability`) is ignored. */
    chooseVariant: (variantId: string) => void;
    closeVariantPicker: () => void;

    /* Discounts */
    /** The whole sale's discount as entered; it survives other cart changes. */
    saleDiscount: Discount | null;
    /** Sets or (with `null`) clears a line's discount; an unknown line is ignored. */
    setLineDiscount: (variantId: string, discount: Discount | null) => void;
    setSaleDiscount: (discount: Discount | null) => void;
    /** A discount that does not fit the cart as it stands, or `null`. */
    discountProblem: DiscountProblem | null;

    /* Totals (derived, GEL) */
    /** The sum of list prices. */
    subtotal: number;
    lineDiscountTotal: number;
    /** What the sale discount takes off what the lines leave. */
    saleDiscountAmount: number;
    /** Line discounts + the sale discount. */
    discountTotal: number;
    /**
     * The charged amount: subtotal − every discount, computed with bos-api's
     * rule. Equals `subtotal` when no discount is set. Meaningless while
     * `discountProblem` is set (it can reach zero or below).
     */
    total: number;
};

const SellerContext = React.createContext<SellerContextValue | null>(null);

export const SellerContextProvider = SellerContext.Provider;

/** Consume the seller POS state. Throws if used outside `<SellerProvider>`. */
export const useSeller = (): SellerContextValue => {
    const context = React.useContext(SellerContext);
    if (!context) {
        throw new Error('useSeller must be used within a SellerProvider');
    }
    return context;
};

const clampQnt = (qnt: number, stock: number): number =>
    Math.max(0, Math.min(Math.floor(qnt), stock));

/** Whether one more of `variant` can go into a cart holding `inCart` of it. */
const availabilityOf = (
    variant: PosVariant,
    inCart: number,
): VariantAvailability => {
    if (variant.sellableQuantity <= 0) {
        return 'outOfStock';
    }
    return inCart >= variant.sellableQuantity ? 'allInCart' : 'available';
};

/** What adding one of a product's named variants would do; `add` carries the variant. */
type AddDecision =
    | { type: 'add'; variant: PosVariant }
    | { type: 'variantPicker'; namedVariantIds: string[] }
    | { type: 'outOfStock' }
    | { type: 'atLimit' };

/**
 * Decides an add from the variants a scan or a search named (all of one
 * product) and what the cart holds of each: nothing sellable is out of stock;
 * every sellable one already all in the cart is at its limit; otherwise one
 * named variant is added and several open the picker.
 */
const decideAdd = (
    named: PosVariant[],
    inCart: (variantId: string) => number,
): AddDecision => {
    const availability = named.map((variant) =>
        availabilityOf(variant, inCart(variant.variantId)),
    );
    if (availability.every((state) => state === 'outOfStock')) {
        return { type: 'outOfStock' };
    }
    if (!availability.includes('available')) {
        return { type: 'atLimit' };
    }
    const [sole, ...others] = named;
    return sole && others.length === 0
        ? { type: 'add', variant: sole }
        : {
              type: 'variantPicker',
              namedVariantIds: named.map((variant) => variant.variantId),
          };
};

/** The open picker: which product, and which of its variants opened it. */
type PickerTarget = {
    productId: string;
    /** The variants a scan named; every variant for a tile tap or a search. */
    namedVariantIds: string[];
};

/** Which of several products sharing a barcode a scan prefers: lower wins. */
const ADD_PREFERENCE: Record<AddDecision['type'], number> = {
    add: 0,
    variantPicker: 0,
    atLimit: 1,
    outOfStock: 2,
};

const normalizeCode = (code: string): string => code.trim().toLowerCase();

const withTrimmedNote = (discount: Discount | null): Discount | null =>
    discount === null ? null : { ...discount, note: discount.note.trim() };

/**
 * Builds the seller POS state + actions over the given product `catalog`. Lives
 * in a hook (not the provider component) so all logic stays in `.ts`;
 * `SellerProvider.tsx` only fetches the catalog and wires the returned value
 * into context. Taking `catalog` as an argument keeps this hook pure and
 * trivially testable (no API/query plumbing needed).
 */
export const useSellerState = (catalog: SellerCatalog): SellerContextValue => {
    const { products, isLoading, isError, truncated } = catalog;

    const [search, setSearch] = React.useState('');
    const [selectedCategoryIds, setSelectedCategoryIds] = React.useState<
        string[]
    >([]);
    const [cartLines, setCart] = React.useState<CartLine[]>([]);
    // Only ids are held: the picker reads the current catalog row, so a
    // refresh while it is open updates its prices and quantities too.
    const [pickerTarget, setPickerTarget] = React.useState<PickerTarget | null>(
        null,
    );

    const filteredProducts = React.useMemo(() => {
        const query = search.trim().toLowerCase();
        return products.filter((product) => {
            const matchesCategory =
                selectedCategoryIds.length === 0 ||
                (product.categoryId !== null &&
                    selectedCategoryIds.includes(product.categoryId));
            // Manual search only — no scanner handling. A variant's label and
            // its barcode are matched as well as the product name, because a
            // seller asked for "black L" or handed a code is not asking about
            // the product's name at all.
            const matchesQuery =
                query === '' ||
                product.name.toLowerCase().includes(query) ||
                product.variants.some(
                    (variant) =>
                        variant.barcode.toLowerCase().includes(query) ||
                        (variant.label?.toLowerCase().includes(query) ?? false),
                );
            return matchesCategory && matchesQuery;
        });
    }, [products, search, selectedCategoryIds]);

    const productById = React.useMemo(
        () => new Map(products.map((product) => [product.id, product])),
        [products],
    );

    // A product that leaves the catalog while its picker is open (archived,
    // or gone from the branch) closes the picker for good, rather than
    // leaving it to spring back open if a later refresh returns the product.
    if (pickerTarget !== null && !productById.has(pickerTarget.productId)) {
        setPickerTarget(null);
    }

    // The catalog refreshes underneath an open cart — every minute, on focus,
    // after a sale — so a line that kept the row it was added with would show a
    // price the sale no longer charges and a quantity the branch no longer has.
    // Each line is re-seeded from its variant's current row instead; a variant
    // that has left the catalog keeps its own snapshot, because dropping the
    // line would erase it from under the seller, where the server's refusal at
    // checkout at least says why.
    const currentLine = React.useCallback(
        (line: CartLine): CartLine => {
            const product = productById.get(line.product.id) ?? line.product;
            const variant =
                product.variants.find(
                    (candidate) =>
                        candidate.variantId === line.variant.variantId,
                ) ?? line.variant;
            return { ...line, product, variant };
        },
        [productById],
    );

    const cart = React.useMemo(
        () => cartLines.map(currentLine),
        [cartLines, currentLine],
    );

    const inCartQty = React.useCallback(
        (productId: string): number =>
            cart
                .filter((line) => line.product.id === productId)
                .reduce((sum, line) => sum + line.qnt, 0),
        [cart],
    );

    const inCartOfVariant = React.useCallback(
        (variantId: string): number =>
            cart.find((line) => line.variant.variantId === variantId)?.qnt ?? 0,
        [cart],
    );

    const addToCart = React.useCallback(
        (product: PosProduct, variant: PosVariant) => {
            if (variant.sellableQuantity <= 0) {
                return;
            }
            setCart((prev) => {
                const inCart = prev.some(
                    (line) => line.variant.variantId === variant.variantId,
                );
                if (!inCart) {
                    return [
                        ...prev,
                        { product, variant, qnt: 1, discount: null },
                    ];
                }
                return prev.map((line) =>
                    line.variant.variantId === variant.variantId
                        ? {
                              ...line,
                              qnt: clampQnt(
                                  line.qnt + 1,
                                  variant.sellableQuantity,
                              ),
                          }
                        : line,
                );
            });
        },
        [],
    );

    const selectProduct = React.useCallback(
        (product: PosProduct): 'added' | 'variantPicker' => {
            const [sole, ...others] = product.variants;
            if (sole && others.length === 0) {
                addToCart(product, sole);
                return 'added';
            }
            // Which variant the customer wants is a question for the seller;
            // the tile never guesses.
            setPickerTarget({
                productId: product.id,
                namedVariantIds: product.variants.map(
                    (variant) => variant.variantId,
                ),
            });
            return 'variantPicker';
        },
        [addToCart],
    );

    const variantPicker = React.useMemo((): VariantPicker | null => {
        const product =
            pickerTarget === null
                ? undefined
                : productById.get(pickerTarget.productId);
        if (!pickerTarget || !product) {
            return null;
        }
        const options = product.variants.map((variant): VariantOption => {
            const inCart = inCartOfVariant(variant.variantId);
            return {
                variantId: variant.variantId,
                // A refresh can leave the open picker with a single
                // variant, which has no label; the product name still
                // names it.
                label: variantLabel(product, variant) ?? product.name,
                price: variant.price,
                sellableQuantity: variant.sellableQuantity,
                inCartQty: inCart,
                availability: availabilityOf(variant, inCart),
            };
        });
        const firstAvailable = (candidates: VariantOption[]) =>
            candidates.find((option) => option.availability === 'available')
                ?.variantId;
        const named = options.filter((option) =>
            pickerTarget.namedVariantIds.includes(option.variantId),
        );
        return {
            product,
            options,
            initialVariantId:
                firstAvailable(named) ?? firstAvailable(options) ?? null,
        };
    }, [pickerTarget, productById, inCartOfVariant]);

    const chooseVariant = React.useCallback(
        (variantId: string) => {
            const option = variantPicker?.options.find(
                (candidate) => candidate.variantId === variantId,
            );
            const variant = variantPicker?.product.variants.find(
                (candidate) => candidate.variantId === variantId,
            );
            if (
                !variantPicker ||
                !variant ||
                option?.availability !== 'available'
            ) {
                return;
            }
            addToCart(variantPicker.product, variant);
            setPickerTarget(null);
        },
        [variantPicker, addToCart],
    );

    const closeVariantPicker = React.useCallback(
        () => setPickerTarget(null),
        [],
    );

    const applyAdd = React.useCallback(
        (product: PosProduct, decision: AddDecision): AddProductOutcome => {
            switch (decision.type) {
                case 'add':
                    addToCart(product, decision.variant);
                    return { type: 'added' };
                case 'variantPicker':
                    setPickerTarget({
                        productId: product.id,
                        namedVariantIds: decision.namedVariantIds,
                    });
                    return { type: 'variantPicker' };
                case 'outOfStock':
                case 'atLimit':
                    return { type: decision.type };
            }
        },
        [addToCart],
    );

    const addProduct = React.useCallback(
        (product: PosProduct): AddProductOutcome =>
            applyAdd(product, decideAdd(product.variants, inCartOfVariant)),
        [applyAdd, inCartOfVariant],
    );

    const addByBarcode = React.useCallback(
        (code: string): AddByBarcodeOutcome => {
            const normalized = normalizeCode(code);
            if (normalized === '') {
                return { type: 'notFound' };
            }
            // Barcode is non-unique (see `PosVariant.barcode`): every product
            // with a variant carrying the code is a candidate, and only its
            // variants carrying the code are what the scan named.
            let best: { product: PosProduct; decision: AddDecision } | null =
                null;
            for (const product of products) {
                const named = product.variants.filter(
                    (variant) => normalizeCode(variant.barcode) === normalized,
                );
                if (named.length === 0) {
                    continue;
                }
                const decision = decideAdd(named, inCartOfVariant);
                if (
                    best === null ||
                    ADD_PREFERENCE[decision.type] <
                        ADD_PREFERENCE[best.decision.type]
                ) {
                    best = { product, decision };
                }
            }
            return best === null
                ? { type: 'notFound' }
                : applyAdd(best.product, best.decision);
        },
        [products, inCartOfVariant, applyAdd],
    );

    const setLineQnt = React.useCallback(
        (variantId: string, qnt: number) => {
            // An emptied number field commits NaN (react-stately): keep the line.
            if (!Number.isFinite(qnt)) {
                return;
            }
            setCart((prev) =>
                prev.flatMap((line) => {
                    if (line.variant.variantId !== variantId) {
                        return [line];
                    }
                    const next = clampQnt(
                        qnt,
                        currentLine(line).variant.sellableQuantity,
                    );
                    return next <= 0 ? [] : [{ ...line, qnt: next }];
                }),
            );
        },
        [currentLine],
    );

    const removeLine = React.useCallback((variantId: string) => {
        setCart((prev) =>
            prev.filter((line) => line.variant.variantId !== variantId),
        );
    }, []);

    const [saleDiscount, setSaleDiscountState] =
        React.useState<Discount | null>(null);

    const setLineDiscount = React.useCallback(
        (variantId: string, discount: Discount | null) => {
            setCart((prev) =>
                prev.map((line) =>
                    line.variant.variantId === variantId
                        ? { ...line, discount: withTrimmedNote(discount) }
                        : line,
                ),
            );
        },
        [],
    );

    const setSaleDiscount = React.useCallback(
        (discount: Discount | null) =>
            setSaleDiscountState(withTrimmedNote(discount)),
        [],
    );

    // Void and a completed sale both end here: every discount goes with the
    // cart, the line ones with their lines.
    const clearCart = React.useCallback(() => {
        setCart([]);
        setSaleDiscountState(null);
    }, []);

    const cartCount = React.useMemo(
        () => cart.reduce((sum, line) => sum + line.qnt, 0),
        [cart],
    );

    const subtotal = React.useMemo(
        () =>
            cart.reduce((sum, line) => sum + line.variant.price * line.qnt, 0),
        [cart],
    );

    const priced = React.useMemo(
        () =>
            priceSale(
                cart.map((line) => ({
                    unitPrice: line.variant.price,
                    quantity: line.qnt,
                    discount: line.discount,
                })),
                saleDiscount,
            ),
        [cart, saleDiscount],
    );

    const discountProblem = React.useMemo((): DiscountProblem | null => {
        if (priced.fit === 'fits') {
            return null;
        }
        const line =
            priced.problemLineIndex === null
                ? undefined
                : cart[priced.problemLineIndex];
        return line
            ? { kind: priced.fit, variantId: line.variant.variantId }
            : { kind: priced.fit };
    }, [priced, cart]);

    const lineDiscountTotal = toGel(priced.lineDiscountTotalTetri);
    const saleDiscountAmount = toGel(priced.saleDiscountTetri);
    const discountTotal = toGel(priced.discountTotalTetri);
    // Without a discount the payable keeps the float subtotal and its existing
    // rounding; with one, the tetri total is the server's figure.
    const hasDiscount =
        saleDiscount !== null || cart.some((line) => line.discount !== null);
    const total = hasDiscount ? toGel(priced.totalTetri) : subtotal;

    return React.useMemo(
        () => ({
            search,
            setSearch,
            selectedCategoryIds,
            setSelectedCategoryIds,
            filteredProducts,
            productsLoading: isLoading,
            productsError: isError,
            productsTruncated: truncated,
            cart,
            cartCount,
            inCartQty,
            selectProduct,
            setLineQnt,
            removeLine,
            clearCart,
            addByBarcode,
            addProduct,
            variantPicker,
            chooseVariant,
            closeVariantPicker,
            saleDiscount,
            setLineDiscount,
            setSaleDiscount,
            discountProblem,
            subtotal,
            lineDiscountTotal,
            saleDiscountAmount,
            discountTotal,
            total,
        }),
        [
            search,
            selectedCategoryIds,
            filteredProducts,
            isLoading,
            isError,
            truncated,
            cart,
            cartCount,
            inCartQty,
            selectProduct,
            setLineQnt,
            removeLine,
            clearCart,
            addByBarcode,
            addProduct,
            variantPicker,
            chooseVariant,
            closeVariantPicker,
            saleDiscount,
            setLineDiscount,
            setSaleDiscount,
            discountProblem,
            subtotal,
            lineDiscountTotal,
            saleDiscountAmount,
            discountTotal,
            total,
        ],
    );
};
